import { useState, useEffect } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import { Loader2, CheckCircle, UserCheck, Eye, EyeOff } from "lucide-react";
import logoImg from "@/assets/images/logo.png";
import { formatCpf } from "@/lib/formatters";

const ROLE_LABELS: Record<string, string> = {
  tv: "Técnico Vistoriador (TV)",
  rt: "Responsável Técnico (RT)",
  admin: "Administrador",
};

function getRoleLabel(roles: string[]): string {
  const labels = roles.map((r) => ROLE_LABELS[r] ?? r);
  return labels.join(" / ");
}

type Step = "code" | "form" | "success";

export default function RegistrarUsuario() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const codeFromUrl = params.get("invite") || "";

  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("code");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [code, setCode] = useState(codeFromUrl);
  const [inviteInfo, setInviteInfo] = useState<{
    roles: string[];
    empresaId: string;
    email?: string;
  } | null>(null);

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [cpf, setCpf] = useState("");
  const [crea, setCrea] = useState("");

  useEffect(() => {
    if (codeFromUrl) {
      handleValidateCode(codeFromUrl);
    }
  }, []);

  const handleValidateCode = async (codeValue?: string) => {
    const codeToValidate = codeValue || code;
    if (!codeToValidate.trim()) {
      setError("Digite o código de convite");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await apiRequest("POST", "/api/user-invites/validate", {
        code: codeToValidate.trim(),
      });
      const data = await res.json();
      if (data.valid) {
        setInviteInfo({
          roles: data.roles,
          empresaId: data.empresaId,
          email: data.email,
        });
        if (data.email) setEmail(data.email);
        setStep("form");
      } else {
        setError("Código inválido ou expirado");
      }
    } catch {
      setError("Código inválido ou expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("As senhas não conferem");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    const isTV = inviteInfo?.roles.includes("tv");
    const isRT = inviteInfo?.roles.includes("rt");

    if ((isTV || isRT) && !cpf.trim()) {
      setError("CPF é obrigatório");
      return;
    }
    if (isRT && !crea.trim()) {
      setError("CREA é obrigatório para Responsável Técnico");
      return;
    }
    setLoading(true);
    try {
      await apiRequest("POST", "/api/auth/register-user", {
        nome,
        email,
        password,
        codigoConvite: code.trim(),
        cpf: cpf.replace(/\D/g, "").trim(),
        ...(isRT && { crea: crea.trim() }),
      });

      await supabase.auth.signInWithPassword({ email, password });

      setStep("success");
    } catch (err: any) {
      let message = "Erro ao processar cadastro";
      try {
        const parsed = JSON.parse(err.message.split(": ").slice(1).join(": "));
        message = parsed.message || message;
      } catch {
        if (err.message?.includes(":")) {
          message = err.message.split(": ").slice(1).join(": ");
        }
      }
      setError(message);
      toast({
        title: "Erro no Cadastro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-md">
          <Card className="p-8 border-none shadow-2xl text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold font-display text-slate-900 mb-2">
              Conta Criada!
            </h1>
            <p className="text-muted-foreground mb-6">
              Sua conta foi criada com sucesso. Bem-vindo ao LaudoLegal!
            </p>
            <Button
              className="w-full gap-2"
              onClick={() => setLocation("/dashboard")}
            >
              Acessar o Painel
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Link href="/">
            <img
              src={logoImg}
              alt="LaudoLegal"
              className="h-14 w-14 object-contain cursor-pointer"
            />
          </Link>
        </div>

        <Card className="p-8 border-none shadow-2xl">
          {step === "code" && (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold font-display text-slate-900">
                  Criar sua Conta
                </h1>
                <p className="text-muted-foreground mt-2">
                  Insira o código de convite que você recebeu.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-code">Código de Convite</Label>
                  <Input
                    id="invite-code"
                    placeholder="Ex: A1B2C3D4"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="bg-slate-50 border-slate-200 text-center text-lg tracking-widest font-mono"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  className="w-full gap-2"
                  onClick={() => handleValidateCode()}
                  disabled={loading || !code.trim()}
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Validar Código
                </Button>

                <div className="text-center pt-2">
                  <Link
                    href="/login"
                    className="text-sm text-primary hover:underline"
                  >
                    Já tem uma conta? Entrar
                  </Link>
                </div>
              </div>
            </>
          )}

          {step === "form" && inviteInfo && (
            <>
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold font-display text-slate-900">
                  Criar sua Conta
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                  Perfil: <strong>{getRoleLabel(inviteInfo.roles)}</strong>
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    placeholder="Seu nome completo"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    required
                  />
                </div>

                {(inviteInfo.roles.includes("tv") ||
                  inviteInfo.roles.includes("rt")) && (
                  <div className="space-y-2">
                    <Label htmlFor="cpf">CPF *</Label>
                    <Input
                      id="cpf"
                      placeholder="000.000.000-00"
                      value={cpf}
                      onChange={(e) => setCpf(formatCpf(e.target.value))}
                      className="bg-slate-50 border-slate-200"
                      required
                    />
                  </div>
                )}

                {inviteInfo.roles.includes("rt") && (
                  <div className="space-y-2">
                    <Label htmlFor="crea">CREA *</Label>
                    <Input
                      id="crea"
                      placeholder="Número do CREA"
                      value={crea}
                      onChange={(e) => setCrea(e.target.value)}
                      className="bg-slate-50 border-slate-200"
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-slate-50 border-slate-200 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar Senha *</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Repita a senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-slate-50 border-slate-200 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full gap-2 mt-2"
                  disabled={loading}
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Criar Conta
                </Button>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("code");
                      setError("");
                    }}
                    className="text-sm text-muted-foreground hover:text-slate-700"
                  >
                    ← Trocar código
                  </button>
                  <Link
                    href="/login"
                    className="text-sm text-primary hover:underline"
                  >
                    Já tem conta? Entrar
                  </Link>
                </div>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
